import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, User, ArrowLeft } from 'lucide-react';

interface PageContent {
  id: string;
  title: string;
  content: string;
  author: string;
  lastModified: string;
  status: string;
  url: string;
}

const PageDisplay: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<PageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      
      // Try to fetch from backend API first
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cms/public/page/${slug || 'home'}`);
        
        if (response.ok) {
          const pageData = await response.json();
          setPage(pageData);
          return;
        }
      } catch (apiError) {
        console.error('API fetch failed, using fallback data:', apiError);
      }
      
      // Fallback to mock data if API fails
      const mockPages: PageContent[] = [
        {
          id: '1',
          title: 'Home',
          content: `
            <div class="hero-section py-5 bg-primary bg-opacity-10">
              <div class="container">
                <div class="row align-items-center">
                  <div class="col-lg-8">
                    <h1 class="display-4 fw-bold text-dark mb-4">Welcome to SkillSeek</h1>
                    <p class="lead text-muted mb-4">Connect with skilled talent in Jamaica. Find the right professionals for your projects or showcase your skills to potential clients.</p>
                    <div class="d-flex gap-3">
                      <a href="/register" class="btn btn-primary btn-lg">Get Started</a>
                      <a href="/about" class="btn btn-outline-primary btn-lg">Learn More</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="features-section py-5">
              <div class="container">
                <div class="row g-4">
                  <div class="col-md-4">
                    <div class="text-center">
                      <div class="bg-primary bg-opacity-10 rounded-circle mx-auto mb-3" style="width: 80px; height: 80px; display: flex; align-items: center; justify-content: center;">
                        <i class="fas fa-users fa-2x text-primary"></i>
                      </div>
                      <h5>Find Talent</h5>
                      <p class="text-muted">Browse through our database of skilled professionals</p>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <div class="bg-primary bg-opacity-10 rounded-circle mx-auto mb-3" style="width: 80px; height: 80px; display: flex; align-items: center; justify-content: center;">
                        <i class="fas fa-handshake fa-2x text-primary"></i>
                      </div>
                      <h5>Secure Booking</h5>
                      <p class="text-muted">Book services with confidence through our platform</p>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <div class="bg-primary bg-opacity-10 rounded-circle mx-auto mb-3" style="width: 80px; height: 80px; display: flex; align-items: center; justify-content: center;">
                        <i class="fas fa-star fa-2x text-primary"></i>
                      </div>
                      <h5>Quality Assurance</h5>
                      <p class="text-muted">All professionals are verified and rated by clients</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          `,
          author: 'Admin',
          lastModified: '2024-01-15T10:30:00Z',
          status: 'published',
          url: '/'
        },
        {
          id: '2',
          title: 'About Us',
          content: `
            <div class="py-5">
              <div class="container">
                <div class="row justify-content-center">
                  <div class="col-lg-8">
                    <h1 class="display-5 fw-bold text-center mb-5">About SkillSeek</h1>
                    
                    <p class="lead">SkillSeek is Jamaica's premier platform connecting skilled professionals with clients who need their services. We bridge the gap between talent and opportunity, making it easier than ever to find the right person for the job.</p>
                    
                    <h3 class="mt-5 mb-3">Our Mission</h3>
                    <p>To empower Jamaican professionals by providing them with a platform to showcase their skills and connect with clients, while helping businesses and individuals find reliable, skilled talent for their projects.</p>
                    
                    <h3 class="mt-5 mb-3">How It Works</h3>
                    <div class="row g-4 mt-4">
                      <div class="col-md-6">
                        <div class="card h-100 border-0 shadow-sm">
                          <div class="card-body">
                            <h5 class="card-title">For Clients</h5>
                            <p class="card-text">Post your project requirements, browse professional profiles, and book services with confidence. Our secure platform ensures quality and reliability.</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="card h-100 border-0 shadow-sm">
                          <div class="card-body">
                            <h5 class="card-title">For Professionals</h5>
                            <p class="card-text">Create your profile, showcase your portfolio, set your rates, and connect with clients looking for your specific skills and expertise.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <h3 class="mt-5 mb-3">Why Choose SkillSeek?</h3>
                    <ul class="list-unstyled">
                      <li class="mb-2"><i class="fas fa-check-circle text-success me-2"></i> Verified professionals</li>
                      <li class="mb-2"><i class="fas fa-check-circle text-success me-2"></i> Secure payment processing</li>
                      <li class="mb-2"><i class="fas fa-check-circle text-success me-2"></i> Customer support</li>
                      <li class="mb-2"><i class="fas fa-check-circle text-success me-2"></i> Local expertise</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          `,
          author: 'Admin',
          lastModified: '2024-01-14T15:45:00Z',
          status: 'published',
          url: '/about'
        }
      ];

      const foundPage = mockPages.find(p => p.url === `/${slug}` || (slug === 'home' && p.url === '/'));
      
      if (foundPage && foundPage.status === 'published') {
        setPage(foundPage);
      } else {
        setError('Page not found');
      }
    } catch (err) {
      setError('Failed to load page');
      console.error('Error fetching page:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <h1 className="display-1 fw-bold text-muted">404</h1>
          <p className="fs-3 text-muted">Page not found</p>
          <a href="/" className="btn btn-primary">
            <ArrowLeft size={16} className="me-2" />
            Go Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Navigation Header */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm sticky-top">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <Shield className="text-primary me-2" size={28} />
            <span className="fw-bold text-dark">SkillSeek</span>
          </a>
          
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <a className="nav-link" href="/">Home</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/about">About</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/services">Services</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/posts">Blog</a>
              </li>
            </ul>
            <div className="d-flex gap-2">
              <a href="/login" className="btn btn-outline-primary">Login</a>
              <a href="/register" className="btn btn-primary">Sign Up</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main>
        <div dangerouslySetInnerHTML={{ __html: page.content }} />
      </main>

      {/* Footer */}
      <footer className="bg-dark text-light py-5 mt-5">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <div className="d-flex align-items-center mb-3">
                <Shield className="text-primary me-2" size={24} />
                <span className="fw-bold">SkillSeek</span>
              </div>
              <p className="text-muted">Connecting skilled professionals with clients across Jamaica.</p>
            </div>
            <div className="col-md-6">
              <div className="row">
                <div className="col-6">
                  <h6 className="fw-bold mb-3">Quick Links</h6>
                  <ul className="list-unstyled">
                    <li><a href="/" className="text-muted text-decoration-none">Home</a></li>
                    <li><a href="/about" className="text-muted text-decoration-none">About</a></li>
                    <li><a href="/services" className="text-muted text-decoration-none">Services</a></li>
                  </ul>
                </div>
                <div className="col-6">
                  <h6 className="fw-bold mb-3">Support</h6>
                  <ul className="list-unstyled">
                    <li><a href="/contact" className="text-muted text-decoration-none">Contact</a></li>
                    <li><a href="/help" className="text-muted text-decoration-none">Help</a></li>
                    <li><a href="/privacy" className="text-muted text-decoration-none">Privacy</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <hr className="my-4" />
          <div className="row align-items-center">
            <div className="col-md-6">
              <small className="text-muted">© 2024 SkillSeek. All rights reserved.</small>
            </div>
            <div className="col-md-6 text-md-end">
              <small className="text-muted d-flex align-items-center justify-content-md-end gap-2">
                <User size={12} />
                Updated by {page.author} on {new Date(page.lastModified).toLocaleDateString()}
              </small>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageDisplay; 